import type { AssertNever } from "@redotech/util/type";
import {
  AnalyticsAggregateOp,
  type AnalyticsSemanticType,
} from "./analytics-query-constants";

/** Ops with the same plain operand shape. */
export const AnalyticsPlainAggregateOps = [
  AnalyticsAggregateOp.Sum,
  AnalyticsAggregateOp.Average,
  AnalyticsAggregateOp.Minimum,
  AnalyticsAggregateOp.Maximum,
  AnalyticsAggregateOp.Median,
  AnalyticsAggregateOp.Spread,
  AnalyticsAggregateOp.Variance,
  AnalyticsAggregateOp.Every,
  AnalyticsAggregateOp.Some,
  AnalyticsAggregateOp.Single,
] as const;

/** Result is boolean, so a boolean position may hold the aggregate itself. */
export const AnalyticsBooleanAggregateOps = [
  AnalyticsAggregateOp.Every,
  AnalyticsAggregateOp.Some,
] as const;

/** Counting ops: same shape as plain, kept separate for readability. */
export const AnalyticsCountAggregateOps = [
  AnalyticsAggregateOp.Count,
  AnalyticsAggregateOp.CountDistinct,
] as const;

/** Ops that take `fraction`. */
export const AnalyticsFractionAggregateOps = [
  AnalyticsAggregateOp.Percentile,
  AnalyticsAggregateOp.DiscretePercentile,
] as const;

/** Ops that take `sequence?`, or `distinct` with `direction?`. */
export const AnalyticsCollectAggregateOps = [
  AnalyticsAggregateOp.List,
] as const;

/**
 * Ops that take a required `by` sequence. `earliest` reads the first row of that order and `latest`
 * its last, so `latest` by ascending terms is the greatest. Every term counts, in order, each with its
 * own direction; rows that tie on all of them fall to the entity identity. Rows with an absent key lose.
 */
export const AnalyticsOrderedAggregateOps = [
  AnalyticsAggregateOp.Earliest,
  AnalyticsAggregateOp.Latest,
] as const;

/** Summaries a `running` relative may compute. Snowflake has no framed median, percentile or count_distinct. */
export const AnalyticsRunningSummaryOps = [
  AnalyticsAggregateOp.Sum,
  AnalyticsAggregateOp.Average,
  AnalyticsAggregateOp.Minimum,
  AnalyticsAggregateOp.Maximum,
  AnalyticsAggregateOp.Spread,
  AnalyticsAggregateOp.Variance,
  AnalyticsAggregateOp.Count,
  AnalyticsAggregateOp.Every,
  AnalyticsAggregateOp.Some,
] as const;

export type AnalyticsPlainAggregateOp =
  (typeof AnalyticsPlainAggregateOps)[number];
export type AnalyticsBooleanAggregateOp =
  (typeof AnalyticsBooleanAggregateOps)[number];
export type AnalyticsCountAggregateOp =
  (typeof AnalyticsCountAggregateOps)[number];
export type AnalyticsFractionAggregateOp =
  (typeof AnalyticsFractionAggregateOps)[number];
export type AnalyticsCollectAggregateOp =
  (typeof AnalyticsCollectAggregateOps)[number];
export type AnalyticsOrderedAggregateOp =
  (typeof AnalyticsOrderedAggregateOps)[number];
export type AnalyticsRunningSummaryOp =
  (typeof AnalyticsRunningSummaryOps)[number];

type AnalyticsGroupedAggregateOp =
  | AnalyticsPlainAggregateOp
  | AnalyticsCountAggregateOp
  | AnalyticsFractionAggregateOp
  | AnalyticsCollectAggregateOp
  | AnalyticsOrderedAggregateOp;

/** Every aggregate op belongs to a group. */
export type AnalyticsAggregateOpsAreGrouped = AssertNever<
  Exclude<AnalyticsAggregateOp, AnalyticsGroupedAggregateOp>
>;

const AnalyticsAnyTypeAggregateOps = [
  AnalyticsAggregateOp.Count,
  AnalyticsAggregateOp.CountDistinct,
  AnalyticsAggregateOp.List,
  AnalyticsAggregateOp.Single,
  AnalyticsAggregateOp.Earliest,
  AnalyticsAggregateOp.Latest,
] as const;

const AnalyticsAdditiveAggregateOps = [
  AnalyticsAggregateOp.Sum,
  AnalyticsAggregateOp.Average,
  AnalyticsAggregateOp.Minimum,
  AnalyticsAggregateOp.Maximum,
  AnalyticsAggregateOp.Median,
  AnalyticsAggregateOp.Percentile,
  AnalyticsAggregateOp.DiscretePercentile,
  AnalyticsAggregateOp.Spread,
  AnalyticsAggregateOp.Variance,
  ...AnalyticsAnyTypeAggregateOps,
] as const;

/** ANSI orders text and ids, so they take the extremes too. */
const AnalyticsOrderedTextAggregateOps = [
  AnalyticsAggregateOp.Minimum,
  AnalyticsAggregateOp.Maximum,
  ...AnalyticsAnyTypeAggregateOps,
] as const;

/** Every semantic type has at least one admissible summary. */
export const AnalyticsAdmissibleAggregates = {
  id: AnalyticsOrderedTextAggregateOps,
  text: AnalyticsOrderedTextAggregateOps,
  category: AnalyticsOrderedTextAggregateOps,
  boolean: [
    AnalyticsAggregateOp.Every,
    AnalyticsAggregateOp.Some,
    ...AnalyticsAnyTypeAggregateOps,
  ],
  date: [
    AnalyticsAggregateOp.Minimum,
    AnalyticsAggregateOp.Maximum,
    ...AnalyticsAnyTypeAggregateOps,
  ],
  datetime: [
    AnalyticsAggregateOp.Minimum,
    AnalyticsAggregateOp.Maximum,
    ...AnalyticsAnyTypeAggregateOps,
  ],
  money: AnalyticsAdditiveAggregateOps,
  quantity: AnalyticsAdditiveAggregateOps,
  duration: AnalyticsAdditiveAggregateOps,
  ratio: [
    AnalyticsAggregateOp.Average,
    AnalyticsAggregateOp.Minimum,
    AnalyticsAggregateOp.Maximum,
    AnalyticsAggregateOp.Median,
    AnalyticsAggregateOp.Percentile,
    AnalyticsAggregateOp.DiscretePercentile,
    AnalyticsAggregateOp.Spread,
    AnalyticsAggregateOp.Variance,
    ...AnalyticsAnyTypeAggregateOps,
  ],
  list: [
    AnalyticsAggregateOp.Count,
    AnalyticsAggregateOp.Single,
    AnalyticsAggregateOp.Earliest,
    AnalyticsAggregateOp.Latest,
  ],
  json: [
    AnalyticsAggregateOp.Count,
    AnalyticsAggregateOp.Single,
    AnalyticsAggregateOp.Earliest,
    AnalyticsAggregateOp.Latest,
  ],
} as const satisfies Record<
  AnalyticsSemanticType,
  readonly AnalyticsAggregateOp[]
>;

export type AnalyticsAdmissibleAggregate<T extends AnalyticsSemanticType> =
  (typeof AnalyticsAdmissibleAggregates)[T][number];

/** The collapse the UI offers first when a finer-grain value is dropped into a coarser result. */
export const AnalyticsDefaultCollapse = {
  id: AnalyticsAggregateOp.CountDistinct,
  text: AnalyticsAggregateOp.List,
  category: AnalyticsAggregateOp.List,
  boolean: AnalyticsAggregateOp.Some,
  date: AnalyticsAggregateOp.Latest,
  datetime: AnalyticsAggregateOp.Latest,
  money: AnalyticsAggregateOp.Sum,
  quantity: AnalyticsAggregateOp.Sum,
  duration: AnalyticsAggregateOp.Sum,
  ratio: AnalyticsAggregateOp.Average,
  list: AnalyticsAggregateOp.Single,
  json: AnalyticsAggregateOp.Single,
} as const satisfies {
  readonly [T in AnalyticsSemanticType]: AnalyticsAdmissibleAggregate<T>;
};

/** Result type of a summary, by op and operand type. */
export type AnalyticsAggregateResult<
  Op extends AnalyticsAggregateOp,
  T extends AnalyticsSemanticType,
> = Op extends "count" | "count_distinct"
  ? "quantity"
  : Op extends "every" | "some"
    ? "boolean"
    : Op extends "list"
      ? "list"
      : Op extends "variance"
        ? T extends "money" | "quantity" | "duration" | "ratio"
          ? "quantity"
          : never
        : Op extends
              | "average"
              | "median"
              | "percentile"
              | "discrete_percentile"
              | "spread"
          ? T extends "money" | "quantity" | "duration" | "ratio"
            ? T
            : never
          : T;
